import React from 'react';
import { companyInfo } from '../data/mock';
import { Lightbulb, Shield, Leaf } from 'lucide-react';

const pillarIcons = {
  'Creative': Lightbulb,
  'Responsible': Shield,
  'Sustainable': Leaf
};

export const ThreePillars = () => {
  return (
    <section className="relative py-32 lg:py-40 bg-gradient-to-b from-[#020617] via-[#042f2e] to-[#020617] overflow-hidden">
      {/* Animated Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyMTIsIDE3NSwgNTUsIDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20 animate-grid-move-smooth" />
      
      {/* Enhanced Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/8 via-transparent to-[#0D9488]/8 animate-gradient-shift-smooth" />
      <div className="absolute inset-0 bg-gradient-to-t from-transparent via-[#D4AF37]/5 to-transparent animate-gradient-wave-pillars" />
      
      {/* Enhanced Floating Particles - More Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[15%] left-[20%] w-2.5 h-2.5 bg-[#D4AF37]/50 rounded-full animate-float-pillars-1 blur-sm" />
        <div className="absolute top-[28%] right-[25%] w-2 h-2 bg-[#0D9488]/50 rounded-full animate-float-pillars-2 blur-sm" />
        <div className="absolute bottom-[20%] right-[20%] w-2 h-2 bg-[#D4AF37]/40 rounded-full animate-float-pillars-3 blur-sm" />
        <div className="absolute top-[55%] left-[28%] w-1.5 h-1.5 bg-[#0D9488]/60 rounded-full animate-float-pillars-4 blur-sm" />
        <div className="absolute bottom-[35%] left-[45%] w-1.5 h-1.5 bg-[#D4AF37]/45 rounded-full animate-float-pillars-5 blur-sm" />
        <div className="absolute top-[42%] right-[38%] w-1 h-1 bg-[#0D9488]/55 rounded-full animate-float-pillars-6 blur-sm" />
        <div className="absolute bottom-[48%] left-[60%] w-2 h-2 bg-[#D4AF37]/50 rounded-full animate-float-pillars-7 blur-sm" />
        <div className="absolute top-[68%] right-[48%] w-1.5 h-1.5 bg-[#0D9488]/45 rounded-full animate-float-pillars-8 blur-sm" />
        <div className="absolute top-[35%] left-[38%] w-1 h-1 bg-[#D4AF37]/55 rounded-full animate-float-pillars-9 blur-sm" />
        <div className="absolute bottom-[55%] right-[55%] w-1.5 h-1.5 bg-[#0D9488]/40 rounded-full animate-float-pillars-10 blur-sm" />
      </div>

      {/* Smooth Light Beams */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-[28%] w-[1px] h-full bg-gradient-to-b from-transparent via-[#D4AF37]/22 to-transparent animate-beam-pillars-1" />
        <div className="absolute top-0 right-[22%] w-[1px] h-full bg-gradient-to-b from-transparent via-[#0D9488]/18 to-transparent animate-beam-pillars-2" />
        <div className="absolute top-0 left-[58%] w-[1px] h-full bg-gradient-to-b from-transparent via-[#D4AF37]/20 to-transparent animate-beam-pillars-3" />
      </div>

      {/* Pulsing Ambient Orbs */}
      <div className="absolute top-1/6 left-1/5 w-80 h-80 bg-[#D4AF37]/12 rounded-full blur-3xl animate-pulse-ambient-pillars-1" />
      <div className="absolute bottom-1/5 right-1/4 w-72 h-72 bg-[#0D9488]/10 rounded-full blur-3xl animate-pulse-ambient-pillars-2" />

      {/* Decorative Lines */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent animate-pulse-line-smooth" />
      <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent animate-pulse-line-smooth" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20 animate-fade-in-up-smooth">
          <div className="flex items-center justify-center gap-6 mb-8">
            <div className="w-16 h-[1px] bg-[#D4AF37] animate-expand-line-smooth" />
            <span className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-light animate-glow-text-pillars">Our Philosophy</span>
            <div className="w-16 h-[1px] bg-[#D4AF37] animate-expand-line-smooth" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6 tracking-tight animate-text-reveal-smooth">
            {companyInfo.tagline}
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-3xl mx-auto font-light animate-fade-in-smooth animation-delay-400">
            Three core principles guide every investment decision we make, ensuring long-term value creation for our partners.
          </p>
        </div>

        {/* Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {companyInfo.pillars.map((pillar, idx) => {
            const Icon = pillarIcons[pillar.title];
            return (
              <div
                key={idx}
                className="group relative bg-[#0f172a]/70 backdrop-blur-md border border-white/10 hover:border-[#D4AF37]/60 p-10 transition-all duration-1000 hover:scale-105 hover:shadow-2xl hover:shadow-[#D4AF37]/25 animate-slide-in-stagger-smooth overflow-hidden"
                style={{ animationDelay: `${idx * 200}ms` }}
              >
                {/* Animated Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 via-[#D4AF37]/8 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
                
                {/* Shimmer Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500" />

                {/* Pulsing Corner Light */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-[#D4AF37]/10 rounded-full blur-2xl group-hover:scale-[2] transition-transform duration-1000" />

                {/* Number */}
                <div className="absolute top-8 right-8 text-7xl font-serif text-white/5 group-hover:text-[#D4AF37]/12 transition-all duration-1000 animate-number-glow-smooth">
                  0{idx + 1}
                </div>

                {/* Icon with Glow */}
                <div className="mb-8 relative">
                  <div className="w-16 h-16 bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/25 transition-all duration-1000 group-hover:scale-110 animate-icon-float-smooth">
                    <Icon className="w-8 h-8 text-[#D4AF37] stroke-[1px] group-hover:drop-shadow-[0_0_15px_rgba(212,175,55,0.7)] transition-all duration-1000" />
                  </div>
                  {/* Pulsing Ring */}
                  <div className="absolute inset-0 border border-[#D4AF37]/20 animate-pulse-ring-smooth" />
                </div>

                {/* Title */}
                <h3 className="text-2xl font-serif text-white mb-4 tracking-tight relative z-10 group-hover:text-[#D4AF37] transition-colors duration-1000">
                  {pillar.title}
                </h3>

                {/* Description */}
                <p className="text-[#94A3B8] leading-relaxed font-light relative z-10 group-hover:text-white/90 transition-colors duration-1000">
                  {pillar.description}
                </p>

                {/* Hover Line */}
                <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gradient-to-r from-[#D4AF37] to-transparent group-hover:w-full transition-all duration-1000" />
              </div>
            );
          })}
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes grid-move-smooth {
          0% { transform: translateY(0); }
          100% { transform: translateY(60px); }
        }

        @keyframes gradient-shift-smooth {
          0%, 100% { opacity: 0.08; }
          50% { opacity: 0.15; }
        }

        @keyframes gradient-wave-pillars {
          0%, 100% { transform: translateY(0); opacity: 0.05; }
          50% { transform: translateY(-25px); opacity: 0.12; }
        }

        @keyframes float-pillars-1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          50% { transform: translate(50px, -70px) scale(1.3); opacity: 0.8; }
        }

        @keyframes float-pillars-2 {
          0%, 100% { transform: translate(0, 0); opacity: 0.5; }
          50% { transform: translate(-60px, 80px); opacity: 0.75; }
        }

        @keyframes float-pillars-3 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.4; }
          50% { transform: translate(70px, -60px) rotate(180deg); opacity: 0.7; }
        }

        @keyframes float-pillars-4 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.6; }
          50% { transform: translate(-50px, -70px) scale(1.2); opacity: 0.85; }
        }

        @keyframes float-pillars-5 {
          0%, 100% { transform: translate(0, 0); opacity: 0.45; }
          50% { transform: translate(55px, 65px); opacity: 0.75; }
        }

        @keyframes float-pillars-6 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.55; }
          50% { transform: translate(-45px, -55px) rotate(200deg); opacity: 0.8; }
        }

        @keyframes float-pillars-7 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          50% { transform: translate(65px, -75px) scale(1.4); opacity: 0.8; }
        }

        @keyframes float-pillars-8 {
          0%, 100% { transform: translate(0, 0); opacity: 0.45; }
          50% { transform: translate(-58px, 68px); opacity: 0.75; }
        }

        @keyframes float-pillars-9 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.55; }
          50% { transform: translate(48px, 58px) rotate(180deg); opacity: 0.8; }
        }

        @keyframes float-pillars-10 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.4; }
          50% { transform: translate(-52px, -62px) scale(1.3); opacity: 0.7; }
        }

        @keyframes beam-pillars-1 {
          0%, 100% { opacity: 0; transform: translateY(-100%); }
          50% { opacity: 0.22; transform: translateY(100%); }
        }

        @keyframes beam-pillars-2 {
          0%, 100% { opacity: 0; transform: translateY(100%); }
          50% { opacity: 0.18; transform: translateY(-100%); }
        }

        @keyframes beam-pillars-3 {
          0%, 100% { opacity: 0; transform: translateY(-80%); }
          50% { opacity: 0.20; transform: translateY(120%); }
        }

        @keyframes pulse-ambient-pillars-1 {
          0%, 100% { opacity: 0.12; transform: scale(1); }
          50% { opacity: 0.20; transform: scale(1.4); }
        }

        @keyframes pulse-ambient-pillars-2 {
          0%, 100% { opacity: 0.10; transform: scale(1); }
          50% { opacity: 0.18; transform: scale(1.45); }
        }

        @keyframes pulse-line-smooth {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }

        @keyframes fade-in-up-smooth {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes expand-line-smooth {
          0% { width: 0; }
          100% { width: 4rem; }
        }

        @keyframes glow-text-pillars {
          0%, 100% { text-shadow: 0 0 12px rgba(212, 175, 55, 0.35); }
          50% { text-shadow: 0 0 24px rgba(212, 175, 55, 0.55); }
        }

        @keyframes text-reveal-smooth {
          0% { opacity: 0; transform: translateY(30px); filter: blur(10px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }

        @keyframes fade-in-smooth {
          0% { opacity: 0; transform: translateY(25px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes slide-in-stagger-smooth {
          0% { opacity: 0; transform: translateY(50px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes number-glow-smooth {
          0%, 100% { opacity: 0.05; }
          50% { opacity: 0.12; }
        }

        @keyframes icon-float-smooth {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        @keyframes pulse-ring-smooth {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.4); opacity: 0; }
        }

        .animate-grid-move-smooth { animation: grid-move-smooth 25s linear infinite; }
        .animate-gradient-shift-smooth { animation: gradient-shift-smooth 15s ease-in-out infinite; }
        .animate-gradient-wave-pillars { animation: gradient-wave-pillars 20s ease-in-out infinite; }
        .animate-float-pillars-1 { animation: float-pillars-1 22s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-pillars-2 { animation: float-pillars-2 24s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-pillars-3 { animation: float-pillars-3 26s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-pillars-4 { animation: float-pillars-4 23s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-pillars-5 { animation: float-pillars-5 25s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-pillars-6 { animation: float-pillars-6 27s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-pillars-7 { animation: float-pillars-7 28s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-pillars-8 { animation: float-pillars-8 26s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-pillars-9 { animation: float-pillars-9 29s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-float-pillars-10 { animation: float-pillars-10 24s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-beam-pillars-1 { animation: beam-pillars-1 18s ease-in-out infinite; }
        .animate-beam-pillars-2 { animation: beam-pillars-2 20s ease-in-out infinite; }
        .animate-beam-pillars-3 { animation: beam-pillars-3 22s ease-in-out infinite; }
        .animate-pulse-ambient-pillars-1 { animation: pulse-ambient-pillars-1 14s ease-in-out infinite; }
        .animate-pulse-ambient-pillars-2 { animation: pulse-ambient-pillars-2 16s ease-in-out infinite; }
        .animate-pulse-line-smooth { animation: pulse-line-smooth 4s ease-in-out infinite; }
        .animate-fade-in-up-smooth { animation: fade-in-up-smooth 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-expand-line-smooth { animation: expand-line-smooth 1.2s ease-out forwards; }
        .animate-glow-text-pillars { animation: glow-text-pillars 4s ease-in-out infinite; }
        .animate-text-reveal-smooth { animation: text-reveal-smooth 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fade-in-smooth { animation: fade-in-smooth 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .animate-slide-in-stagger-smooth { animation: slide-in-stagger-smooth 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .animate-number-glow-smooth { animation: number-glow-smooth 5s ease-in-out infinite; }
        .animate-icon-float-smooth { animation: icon-float-smooth 4s ease-in-out infinite; }
        .animate-pulse-ring-smooth { animation: pulse-ring-smooth 2.5s ease-out infinite; }
        
        .animation-delay-400 { animation-delay: 0.4s; }
      `}</style>
    </section>
  );
};

export default ThreePillars;
